import { log } from './utils.js';
import {
  easeTo2d,
  setMapTerrain,
  getOnPathLineString
} from './mapPage.js';

import {
  mapInstance as map,
  mapboxgl
} from './mapPage.js';

const $ = window.jQuery;


// -----------------------------------------------------------------------------
// MODULE STATE
// -----------------------------------------------------------------------------

let routePreviewFrames = [];

let routePreviewMeta = {
  totalKm: 0,
  stepKm: 0
};

window.ironman.extraAltitude = 0;

const toRad = Math.PI / 180;


// -----------------------------------------------------------------------------
// GEO HELPERS
// -----------------------------------------------------------------------------

function haversine([lng1, lat1], [lng2, lat2]) {
  const dLat = (lat2 - lat1) * toRad;
  const dLon = (lng2 - lng1) * toRad;

  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * toRad) *
    Math.cos(lat2 * toRad) *
    Math.sin(dLon / 2) ** 2;

  return 2 * 6371 * Math.asin(Math.sqrt(a));
}


function getBearing([lng1, lat1], [lng2, lat2]) {
  const φ1 = lat1 * toRad;
  const φ2 = lat2 * toRad;
  const dLon = (lng2 - lng1) * toRad;

  const y =
    Math.sin(dLon) *
    Math.cos(φ2);

  const x =
    Math.cos(φ1) * Math.sin(φ2) -
    Math.sin(φ1) *
    Math.cos(φ2) *
    Math.cos(dLon);

  return (
    Math.atan2(y, x) *
    180 /
    Math.PI +
    360
  ) % 360;
}


function smoothBearing(current, target, factor) {
  const diff =
    ((target - current + 540) % 360) - 180;

  return (
    current +
    diff * factor +
    360
  ) % 360;
}


function interpolateBearing(a, b, fraction) {
  const diff =
    ((b - a + 540) % 360) - 180;

  return (
    a +
    diff * fraction +
    360
  ) % 360;
}


function destinationPoint(
  [lng, lat],
  distanceKm,
  bearingDeg
) {
  const R = 6371;
  const δ = distanceKm / R;
  const θ = bearingDeg * toRad;

  const φ1 = lat * toRad;
  const λ1 = lng * toRad;

  const φ2 = Math.asin(
    Math.sin(φ1) * Math.cos(δ) +
    Math.cos(φ1) *
    Math.sin(δ) *
    Math.cos(θ)
  );

  const λ2 =
    λ1 +
    Math.atan2(
      Math.sin(θ) *
      Math.sin(δ) *
      Math.cos(φ1),

      Math.cos(δ) -
      Math.sin(φ1) *
      Math.sin(φ2)
    );

  return [
    λ2 * 180 / Math.PI,
    φ2 * 180 / Math.PI
  ];
}


function findPointAt(route, cumDist, targetKm) {
  const total =
    cumDist[cumDist.length - 1];

  const d =
    Math.max(
      0,
      Math.min(targetKm, total)
    );

  let low = 0;
  let high = cumDist.length - 1;

  while (low < high) {
    const mid =
      Math.floor((low + high) / 2);

    if (cumDist[mid] < d) {
      low = mid + 1;
    } else {
      high = mid;
    }
  }

  let i =
    Math.max(1, low) - 1;

  while (
    i < route.length - 2 &&
    cumDist[i + 1] === cumDist[i]
  ) {
    i++;
  }

  const d0 = cumDist[i];
  const d1 = cumDist[i + 1];

  const segmentLength =
    d1 - d0;

  const frac =
    segmentLength > 0
      ? (d - d0) / segmentLength
      : 0;

  const [
    lng1,
    lat1,
    alt1 = 0
  ] = route[i];

  const [
    lng2,
    lat2,
    alt2 = 0
  ] = route[i + 1];

  return {
    lng:
      lng1 +
      (lng2 - lng1) * frac,

    lat:
      lat1 +
      (lat2 - lat1) * frac,

    alt:
      alt1 +
      (alt2 - alt1) * frac
  };
}


// -----------------------------------------------------------------------------
// ANIMATION HELPERS
// -----------------------------------------------------------------------------

/**
 * Smooth acceleration at the beginning of the settle, then transitions
 * into linear motion so the camera still has velocity when the actual
 * route animation takes over.
 *
 * This avoids:
 *
 * move -> stop -> move
 *
 * at the flyTo/route handoff.
 */
function easeIntoLinear(t) {
  const transitionPoint = 0.35;

  if (t < transitionPoint) {
    const x =
      t / transitionPoint;

    const smoothStep =
      x * x * (3 - 2 * x);

    return (
      transitionPoint *
      smoothStep
    );
  }

  return t;
}


function getCameraAltitude(
  lng,
  lat,
  fallbackAlt,
  heightOffset
) {
  let terrainAlt =
    map.queryTerrainElevation([
      lng,
      lat
    ]);

  if (
    terrainAlt === null ||
    terrainAlt === undefined ||
    Number.isNaN(terrainAlt)
  ) {
    terrainAlt =
      fallbackAlt -
      heightOffset;
  }

  return (
    terrainAlt +
    heightOffset +
    window.ironman.extraAltitude
  );
}


function getRouteCameraPosition(
  lng,
  lat,
  alt,
  bearing,
  cameraDistanceKm
) {
  const oppositeBearing =
    (bearing + 180) % 360;

  const [
    camLng,
    camLat
  ] =
    destinationPoint(
      [lng, lat],
      cameraDistanceKm,
      oppositeBearing
    );

  return (
    mapboxgl.MercatorCoordinate
      .fromLngLat(
        {
          lng: camLng,
          lat: camLat
        },
        alt
      )
  );
}


// -----------------------------------------------------------------------------
// PRECOMPUTATION
// -----------------------------------------------------------------------------

export function preComputeRoutePreview(
  route,
  opts = {}
) {
  const {
    stepKm = 0.02,
    heightOffset = 150,

    // Total direction-sampling window.
    // 0.05 = approximately 50m.
    bearingLookAheadKm = 0.05
  } = opts;


  if (
    !Array.isArray(route) ||
    route.length < 2
  ) {
    routePreviewFrames = [];

    routePreviewMeta = {
      totalKm: 0,
      stepKm
    };

    return;
  }


  const cumDist = [0];


  for (
    let i = 1;
    i < route.length;
    i++
  ) {
    cumDist[i] =
      cumDist[i - 1] +
      haversine(
        route[i - 1],
        route[i]
      );
  }


  const totalKm =
    cumDist[cumDist.length - 1];


  if (totalKm === 0) {
    routePreviewFrames = [];

    routePreviewMeta = {
      totalKm: 0,
      stepKm
    };

    return;
  }


  const count =
    Math.ceil(
      totalKm / stepKm
    );


  const frames = [];


  for (
    let i = 0;
    i <= count;
    i++
  ) {
    const dist =
      Math.min(
        i * stepKm,
        totalKm
      );


    const pt =
      findPointAt(
        route,
        cumDist,
        dist
      );


    let beforeDist =
      Math.max(
        0,
        dist - bearingLookAheadKm / 2
      );


    let afterDist =
      Math.min(
        totalKm,
        dist + bearingLookAheadKm / 2
      );


    if (afterDist === beforeDist) {
      if (dist <= 0) {
        afterDist =
          Math.min(
            totalKm,
            bearingLookAheadKm
          );
      } else {
        beforeDist =
          Math.max(
            0,
            totalKm - bearingLookAheadKm
          );
      }
    }


    const before =
      findPointAt(
        route,
        cumDist,
        beforeDist
      );


    const after =
      findPointAt(
        route,
        cumDist,
        afterDist
      );


    const bearing =
      getBearing(
        [
          before.lng,
          before.lat
        ],
        [
          after.lng,
          after.lat
        ]
      );


    frames.push({
      lng: pt.lng,
      lat: pt.lat,

      alt:
        pt.alt +
        heightOffset,

      bearing
    });
  }


  routePreviewFrames =
    frames;


  routePreviewMeta = {
    totalKm,
    stepKm
  };
}


export function getPrecomputedRoutePreview() {
  return routePreviewFrames;
}


export function getPrecomputedRouteMeta() {
  return routePreviewMeta;
}


// -----------------------------------------------------------------------------
// ROUTE PREVIEW ANIMATION
// -----------------------------------------------------------------------------

export function doMapboxFreeCameraAnimation(
  opts = {}
) {
  const {
    // Milliseconds per kilometer.
    // Lower = faster.
    speedFactor = 8000,

    // Camera distance behind the current route position.
    cameraDistanceKm = 0.25,

    // Camera rotation smoothing.
    bearingSmoothFactor = 0.02,

    // Arrow rotation smoothing.
    arrowBearingSmoothFactor = 0.12,

    // Approximately 50m direction-sampling window.
    bearingLookAheadKm = 0.05,

    // Use this if the source PNG does not point straight upward.
    arrowRotationOffset = 0,

    // Initial Mapbox flyTo duration.
    flyToDuration = 2000,

    // Transition between flyTo and actual route movement.
    introSettleDuration = 700,

    initialPitch = 72,

    heightOffset = 150
  } = opts;


  // ---------------------------------------------------------------------------
  // UI
  // ---------------------------------------------------------------------------

  $('[data-label="map-UI-content"]')
    .addClass('d-none');

  $('[data-label="end-route-preview-button"]')
    .removeClass('d-none');


  // ---------------------------------------------------------------------------
  // STOP EXISTING ANIMATION
  // ---------------------------------------------------------------------------

  if (window._trailAnimationCancel) {
    window._trailAnimationCancel();

    window._trailAnimationCancel =
      null;

    easeTo2d();

    setMapTerrain(1);


    $('[data-label="map-UI-content"]')
      .removeClass('d-none');

    $('[data-label="end-route-preview-button"]')
      .addClass('d-none');

    return;
  }


  setMapTerrain(1.2);


  // ---------------------------------------------------------------------------
  // ROUTE DATA
  // ---------------------------------------------------------------------------

  const route =
    getOnPathLineString();


  preComputeRoutePreview(
    route,
    {
      ...opts,
      heightOffset,
      bearingLookAheadKm
    }
  );


  const frames =
    getPrecomputedRoutePreview();


  const {
    totalKm
  } =
    getPrecomputedRouteMeta();


  if (
    !frames.length ||
    totalKm === 0
  ) {
    $('[data-label="map-UI-content"]')
      .removeClass('d-none');

    $('[data-label="end-route-preview-button"]')
      .addClass('d-none');

    return log(
      'No frames to animate'
    );
  }


  const duration =
    totalKm *
    speedFactor;


  // ---------------------------------------------------------------------------
  // ARROW MARKER
  // ---------------------------------------------------------------------------

  /*
   * OUTER ELEMENT:
   * controlled by Mapbox for positioning and rotation.
   *
   * INNER IMAGE:
   * controlled by us for scale/opacity animation.
   *
   * Keeping these separate prevents our CSS scale transform from interfering
   * with Mapbox's marker transforms.
   */

  const hikerMarkerElement =
    document.createElement('div');


  hikerMarkerElement.style.width =
    '75px';

  hikerMarkerElement.style.height =
    '81px';

  hikerMarkerElement.style.pointerEvents =
    'none';


  const hikerImg =
    document.createElement('img');


  hikerImg.src =
    '/wp-content/themes/ironman/public/media/routePreviewArrowShadow2.png';


  hikerImg.style.width =
    '100%';

  hikerImg.style.height =
    '100%';

  hikerImg.style.display =
    'block';

  hikerImg.style.pointerEvents =
    'none';

  hikerImg.style.userSelect =
    'none';

  hikerImg.style.transformOrigin =
    '50% 50%';

  hikerImg.style.willChange =
    'transform, opacity';


  /*
   * Start partially scaled rather than extremely tiny.
   *
   * This makes the entrance more subtle and reduces the visual impression
   * of a "pop" when the marker reaches full size.
   */
  hikerImg.style.opacity =
    '0';

  hikerImg.style.transform =
    'scale(0.35)';


  /*
   * No overshoot.
   *
   * The previous spring bezier intentionally grew beyond scale(1), which
   * could make the camera transition itself appear to jerk.
   *
   * This curve approaches 1 smoothly without exceeding it.
   */
  hikerImg.style.transition =
    [
      'transform 450ms cubic-bezier(0.22, 1, 0.36, 1)',
      'opacity 220ms ease-out'
    ].join(', ');


  hikerMarkerElement.appendChild(
    hikerImg
  );


  const hikerMarker =
    new mapboxgl.Marker({
      element:
        hikerMarkerElement,

      rotationAlignment:
        'map',

      pitchAlignment:
        'viewport'
    })
      .setLngLat([
        frames[0].lng,
        frames[0].lat
      ])
      .setRotation(
        frames[0].bearing +
        arrowRotationOffset
      )
      .addTo(map);


  // ---------------------------------------------------------------------------
  // ANIMATION STATE
  // ---------------------------------------------------------------------------

  let start = null;

  let cancelled = false;

  let cameraBearing =
    frames[0].bearing;

  let arrowBearing =
    frames[0].bearing;


  window._trailAnimationCancel =
    () => {
      cancelled = true;
    };


  // ---------------------------------------------------------------------------
  // ROUTE ANIMATION
  // ---------------------------------------------------------------------------

  function animate(ts) {
    if (cancelled) {
      hikerMarker.remove();
      return;
    }


    if (start === null) {
      start = ts;
    }


    const tRaw =
      Math.min(
        (ts - start) / duration,
        1
      );


    const idxF =
      tRaw *
      (frames.length - 1);


    const i =
      Math.floor(idxF);


    const frac =
      idxF - i;


    const a =
      frames[i];


    const b =
      frames[
        Math.min(
          i + 1,
          frames.length - 1
        )
      ];


    // -------------------------------------------------------------------------
    // POSITION
    // -------------------------------------------------------------------------

    const lng =
      a.lng +
      (b.lng - a.lng) *
      frac;


    const lat =
      a.lat +
      (b.lat - a.lat) *
      frac;


    const fallbackAlt =
      a.alt +
      (b.alt - a.alt) *
      frac;


    const alt =
      getCameraAltitude(
        lng,
        lat,
        fallbackAlt,
        heightOffset
      );


    // -------------------------------------------------------------------------
    // ROUTE BEARING
    // -------------------------------------------------------------------------

    const rawBearing =
      interpolateBearing(
        a.bearing,
        b.bearing,
        frac
      );


    // -------------------------------------------------------------------------
    // CAMERA BEARING
    // -------------------------------------------------------------------------

    cameraBearing =
      smoothBearing(
        cameraBearing,
        rawBearing,
        bearingSmoothFactor
      );


    // -------------------------------------------------------------------------
    // ARROW BEARING
    // -------------------------------------------------------------------------

    arrowBearing =
      smoothBearing(
        arrowBearing,
        rawBearing,
        arrowBearingSmoothFactor
      );


    // -------------------------------------------------------------------------
    // CAMERA
    // -------------------------------------------------------------------------

    const cameraPosition =
      getRouteCameraPosition(
        lng,
        lat,
        alt,
        cameraBearing,
        cameraDistanceKm
      );


    const cam =
      map.getFreeCameraOptions();


    cam.position =
      cameraPosition;


    cam.lookAtPoint({
      lng,
      lat
    });


    cam.setPitchBearing(
      initialPitch,
      cameraBearing
    );


    map.setFreeCameraOptions(
      cam
    );


    // -------------------------------------------------------------------------
    // ARROW
    // -------------------------------------------------------------------------

    hikerMarker
      .setLngLat([
        lng,
        lat
      ])
      .setRotation(
        arrowBearing +
        arrowRotationOffset
      );


    // -------------------------------------------------------------------------
    // CONTINUE / FINISH
    // -------------------------------------------------------------------------

    if (tRaw < 1) {
      requestAnimationFrame(
        animate
      );

      return;
    }


    hikerMarker.remove();


    window._trailAnimationCancel =
      null;


    setMapTerrain(1);


    easeTo2d();


    $('[data-label="map-UI-content"]')
      .removeClass('d-none');


    $('[data-label="end-route-preview-button"]')
      .addClass('d-none');
  }


  // ---------------------------------------------------------------------------
  // SETTLE FROM FLYTO INTO EXACT ROUTE CAMERA
  // ---------------------------------------------------------------------------

  function settleIntoRouteCamera(
    callback
  ) {
    const startFrame =
      frames[0];


    const startAlt =
      getCameraAltitude(
        startFrame.lng,
        startFrame.lat,
        startFrame.alt,
        heightOffset
      );


    /*
     * This is the exact camera position that frame zero of the route
     * animation expects.
     */
    const targetPosition =
      getRouteCameraPosition(
        startFrame.lng,
        startFrame.lat,
        startAlt,
        startFrame.bearing,
        cameraDistanceKm
      );


    /*
     * Capture the camera exactly where flyTo left it.
     */
    const currentCamera =
      map.getFreeCameraOptions();


    const startPosition =
      currentCamera.position;


    if (!startPosition) {
      callback();
      return;
    }


    const settleStart =
      performance.now();


    /*
     * Arrow scale-in begins at the start of the settle transition.
     *
     * It finishes at ~450ms, while the camera settle continues until ~700ms.
     *
     * This means the arrow reaches its resting size BEFORE the route begins,
     * so its final scale frame does not coincide with the camera handoff.
     */
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (cancelled) {
          return;
        }

        hikerImg.style.opacity =
          '1';

        hikerImg.style.transform =
          'scale(1)';
      });
    });


    function settleFrame(now) {
      if (cancelled) {
        hikerMarker.remove();
        return;
      }


      const t =
        Math.min(
          (now - settleStart) /
          introSettleDuration,
          1
        );


      /*
       * Crucially, this does NOT ease all the way to zero velocity.
       *
       * It accelerates smoothly initially, then becomes linear toward the end,
       * which makes the transition into route movement feel more continuous.
       */
      const eased =
        easeIntoLinear(t);


      const x =
        startPosition.x +
        (
          targetPosition.x -
          startPosition.x
        ) *
        eased;


      const y =
        startPosition.y +
        (
          targetPosition.y -
          startPosition.y
        ) *
        eased;


      const z =
        startPosition.z +
        (
          targetPosition.z -
          startPosition.z
        ) *
        eased;


      const cam =
        map.getFreeCameraOptions();


      cam.position =
        new mapboxgl.MercatorCoordinate(
          x,
          y,
          z
        );


      cam.lookAtPoint({
        lng: startFrame.lng,
        lat: startFrame.lat
      });


      cam.setPitchBearing(
        initialPitch,
        startFrame.bearing
      );


      map.setFreeCameraOptions(
        cam
      );


      if (t < 1) {
        requestAnimationFrame(
          settleFrame
        );

        return;
      }


      /*
       * Put the camera mathematically at the exact route-start state.
       *
       * Since the settle already arrived here visually, this should not
       * produce a visible snap.
       */
      const finalCam =
        map.getFreeCameraOptions();


      finalCam.position =
        targetPosition;


      finalCam.lookAtPoint({
        lng: startFrame.lng,
        lat: startFrame.lat
      });


      finalCam.setPitchBearing(
        initialPitch,
        startFrame.bearing
      );


      map.setFreeCameraOptions(
        finalCam
      );


      cameraBearing =
        startFrame.bearing;


      arrowBearing =
        startFrame.bearing;


      callback();
    }


    requestAnimationFrame(
      settleFrame
    );
  }


  // ---------------------------------------------------------------------------
  // INITIAL FLYTO
  // ---------------------------------------------------------------------------

  const startFrame =
    frames[0];


  const oppStart =
    (
      startFrame.bearing +
      180
    ) % 360;


  /*
   * Retained from the original implementation.
   *
   * Because this is a negative distance on the opposite bearing, it moves
   * the visual flyTo center forward along the route.
   *
   * The settle transition handles the exact camera match afterwards.
   */
  const negDistance =
    -0.15;


  const [
    startCamLng,
    startCamLat
  ] =
    destinationPoint(
      [
        startFrame.lng,
        startFrame.lat
      ],
      negDistance,
      oppStart
    );


  map.flyTo({
    center: [
      startCamLng,
      startCamLat
    ],

    pitch:
      initialPitch,

    bearing:
      startFrame.bearing,

    essential:
      true,

    duration:
      flyToDuration,

    zoom:
      17
  });


  // ---------------------------------------------------------------------------
  // FLYTO -> CAMERA SETTLE + ARROW SCALE -> ROUTE
  // ---------------------------------------------------------------------------

  map.once(
    'moveend',
    () => {
      if (cancelled) {
        hikerMarker.remove();
        return;
      }


      settleIntoRouteCamera(
        () => {
          if (cancelled) {
            hikerMarker.remove();
            return;
          }


          /*
           * Route timing begins only after:
           *
           * 1. flyTo is complete
           * 2. camera has settled into the exact chase position
           * 3. arrow has finished scaling into place
           */
          start = null;


          requestAnimationFrame(
            animate
          );
        }
      );
    }
  );
}


// window.doMapboxFreeCameraAnimation = doMapboxFreeCameraAnimation;